/**
 * Spec-level structural validator with machine-readable issue codes.
 *
 * Zod tells you "this prop is the wrong type". This validator tells
 * you "the AI put `visible` inside props instead of top-level" — the
 * kind of recurring AI slip that produces a syntactically-valid spec
 * which renders the wrong thing.
 *
 * Mirrors `vercel-labs/json-render`'s validateSpec but adapted to
 * Dash's tree shape (children: DashboardElement[]) instead of the
 * flat root+elements map. Codes are stable so callers (the renderer's
 * dev-mode warning badge, future agent retry logic, devtools panel)
 * can react programmatically.
 */

import type { DashboardSpec } from "../dashboard/catalog"

export type SpecIssueSeverity = "error" | "warning"

/** Stable issue codes — match on these in callers, never on `message`. */
export type SpecIssueCode =
  | "empty_spec"
  | "non_object_root"
  | "children_not_array"
  | "unknown_type"
  | "visible_in_props"
  | "watch_in_props"
  | "repeat_in_props"
  | "on_in_props"
  | "repeat_missing_source"
  | "repeat_missing_item"
  | "tabs_missing_value"
  | "tabs_duplicate_value"
  | "binding_target_missing"
  | "unknown_check_type"

export interface SpecIssue {
  severity: SpecIssueSeverity
  code: SpecIssueCode
  /** Human-readable; for logs and the dev badge. Don't switch on this. */
  message: string
  /** Dotted path into the spec (e.g. "children[2].tabs[0]"). */
  elementPath?: string
}

export interface SpecValidationResult {
  valid: boolean
  issues: SpecIssue[]
}

/**
 * Validation checks the runtime can resolve (built-ins from
 * `utils/validation.ts` plus any host-registered customs). The
 * spec-validator checks against this list ahead of render so
 * `evaluateChecks` can't silently skip a typo'd `{type: "requierd"}`.
 *
 * Custom checks registered via `registerValidationCheck` are passed
 * in by the caller — without them we still flag built-ins-only.
 */
const BUILTIN_CHECK_TYPES = new Set<string>([
  "required",
  "minLength",
  "maxLength",
  "min",
  "max",
  "pattern",
  "email",
  "url",
  "equals",
  "oneOf",
])

function isKnownCheckType(type: string): boolean {
  return BUILTIN_CHECK_TYPES.has(type)
}

/** Component types Dash knows about today. Keep in sync with catalog. */
const KNOWN_TYPES = new Set<string>([
  "BarChart",
  "LineChart",
  "AreaChart",
  "PieChart",
  "KPICard",
  "Table",
  "Text",
  "Separator",
  "Alert",
  "ProgressBar",
  "Grid",
  "Row",
  "Tabs",
  "Accordion",
  "Select",
  "Checkbox",
  "TextInput",
  "Repeat",
])

/**
 * Validate a Dash spec for structural integrity. Returns an issue
 * list; `valid` is true only when there are zero `error`-severity
 * issues. Warnings indicate something the AI emitted that's
 * recoverable but suspicious.
 */
export function validateSpec(spec: unknown): SpecValidationResult {
  const issues: SpecIssue[] = []

  if (!spec || typeof spec !== "object" || Array.isArray(spec)) {
    issues.push({
      severity: "error",
      code: "non_object_root",
      message: "Spec root must be an object",
    })
    return { valid: false, issues }
  }

  const s = spec as Partial<DashboardSpec> & Record<string, unknown>

  if (!Array.isArray(s.children)) {
    issues.push({
      severity: "error",
      code: "children_not_array",
      message: "spec.children must be an array",
    })
    return { valid: false, issues }
  }

  if (s.children.length === 0) {
    issues.push({
      severity: "warning",
      code: "empty_spec",
      message: "Spec has no children — nothing will render",
    })
  }

  for (let i = 0; i < s.children.length; i++) {
    walkElement(s.children[i], `children[${i}]`, issues)
  }

  // Cross-reference query/watch bindings against the actual element
  // tree so a stale path produces a `binding_target_missing` warning
  // (the renderer falls back to AI rebuild, which works but is slow
  // and silent today).
  if (Array.isArray(s.queryBindings)) {
    const allPaths = collectElementPaths(s.children as Record<string, unknown>[])
    for (const b of s.queryBindings as Array<{ element_path?: string; binding_id?: string }>) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (typeof b?.element_path !== "string") continue
      // Strip the trailing "/data" or whatever target_field — bindings
      // attach to a tile, not to a specific prop on it.
      const tilePath = b.element_path.replace(/\.[^.]+$/, "")
      if (!allPaths.has(b.element_path) && !allPaths.has(tilePath)) {
        issues.push({
          severity: "warning",
          code: "binding_target_missing",
          message: `Binding ${b.binding_id ?? "(no id)"} targets ${b.element_path}, which doesn't exist in children`,
          elementPath: b.element_path,
        })
      }
    }
  }

  const valid = issues.every((i) => i.severity !== "error")
  return { valid, issues }
}

// ── helpers ────────────────────────────────────────────────────────

function walkElement(value: unknown, path: string, issues: SpecIssue[]): void {
  if (!value || typeof value !== "object" || Array.isArray(value)) return

  const el = value as Record<string, unknown>
  const type = typeof el.type === "string" ? (el.type as string) : null

  if (type !== null && !KNOWN_TYPES.has(type)) {
    issues.push({
      severity: "warning",
      code: "unknown_type",
      message: `Unknown element type "${type}" — renderer will skip it`,
      elementPath: path,
    })
  }

  // Misplacement checks. Dash schemas put `visible`/`watch`/`repeat`
  // at element top level — a `props.visible` etc. is the AI mistake
  // we want to catch.
  if (el.props && typeof el.props === "object" && !Array.isArray(el.props)) {
    const props = el.props as Record<string, unknown>
    if ("visible" in props) {
      issues.push(misplaced("visible_in_props", "visible", path))
    }
    if ("watch" in props) {
      issues.push(misplaced("watch_in_props", "watch", path))
    }
    if ("repeat" in props) {
      issues.push(misplaced("repeat_in_props", "repeat", path))
    }
    // Event handlers: any key starting with "on" that's an object/string
    // shouldn't live in props for Dash (we use action-catalog dispatch).
    for (const k of Object.keys(props)) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (k.startsWith("on") && k.length > 2 && k[2] === k[2]?.toUpperCase()) {
        issues.push(misplaced("on_in_props", k, path))
      }
    }
  }

  // Field-level validation: catch typo'd check types up front so the
  // input doesn't appear to validate when in fact `evaluateChecks`
  // silently skipped an unknown rule.
  if (Array.isArray(el.checks)) {
    for (let i = 0; i < el.checks.length; i++) {
      const check = el.checks[i]
      if (!check || typeof check !== "object" || Array.isArray(check)) continue
      const checkType = (check as Record<string, unknown>).type
      if (typeof checkType !== "string") continue
      if (!isKnownCheckType(checkType)) {
        issues.push({
          severity: "warning",
          code: "unknown_check_type",
          message: `Unknown validation check "${checkType}" — skipped at runtime`,
          elementPath: `${path}.checks[${i}]`,
        })
      }
    }
  }

  // Repeat-specific structural checks.
  if (type === "Repeat") {
    if (!("source" in el)) {
      issues.push({
        severity: "error",
        code: "repeat_missing_source",
        message: "Repeat element requires `source`",
        elementPath: path,
      })
    }
    if (!("item" in el)) {
      issues.push({
        severity: "error",
        code: "repeat_missing_item",
        message: "Repeat element requires `item` template",
        elementPath: path,
      })
    }
  }

  // Tabs need unique values per tab and every tab must have one.
  if (type === "Tabs" && Array.isArray(el.tabs)) {
    const seen = new Set<string>()
    el.tabs.forEach((tab, i) => {
      if (!tab || typeof tab !== "object" || Array.isArray(tab)) return
      const t = tab as Record<string, unknown>
      const tabPath = `${path}.tabs[${i}]`
      if (typeof t.value !== "string" || t.value === "") {
        issues.push({
          severity: "error",
          code: "tabs_missing_value",
          message: "Tab is missing a non-empty `value`",
          elementPath: tabPath,
        })
      } else if (seen.has(t.value)) {
        issues.push({
          severity: "warning",
          code: "tabs_duplicate_value",
          message: `Duplicate tab value "${t.value}" — selection will be ambiguous`,
          elementPath: tabPath,
        })
      } else {
        seen.add(t.value)
      }
      if (Array.isArray(t.children)) {
        for (let j = 0; j < t.children.length; j++) {
          walkElement(t.children[j], `${tabPath}.children[${j}]`, issues)
        }
      }
    })
  }

  // Recurse into structural children.
  if (Array.isArray(el.children)) {
    for (let i = 0; i < el.children.length; i++) {
      walkElement(el.children[i], `${path}.children[${i}]`, issues)
    }
  }
  // Accordion items.
  if (type === "Accordion" && Array.isArray(el.items)) {
    el.items.forEach((item, i) => {
      if (!item || typeof item !== "object") return
      const it = item as Record<string, unknown>
      if (Array.isArray(it.children)) {
        for (let j = 0; j < it.children.length; j++) {
          walkElement(it.children[j], `${path}.items[${i}].children[${j}]`, issues)
        }
      }
    })
  }
  // Repeat: walk the item template so misplacement checks reach inside.
  if (type === "Repeat" && el.item && typeof el.item === "object") {
    walkElement(el.item, `${path}.item`, issues)
  }
}

function misplaced(
  code: "visible_in_props" | "watch_in_props" | "repeat_in_props" | "on_in_props",
  key: string,
  path: string
): SpecIssue {
  return {
    severity: "warning",
    code,
    message: `\`${key}\` should live at the element's top level, not inside \`props\``,
    elementPath: path,
  }
}

/**
 * Collect every dotted path to an element in the tree, mirroring the
 * convention used by queryBinding.element_path.
 */
function collectElementPaths(
  children: Record<string, unknown>[],
  prefix = "children",
  out: Set<string> = new Set()
): Set<string> {
  for (let i = 0; i < children.length; i++) {
    const path = `${prefix}[${i}]`
    out.add(path)
    const el = children[i]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!el || typeof el !== "object") continue

    if (Array.isArray((el as Record<string, unknown>).children)) {
      collectElementPaths(
        (el as Record<string, unknown>).children as Record<string, unknown>[],
        `${path}.children`,
        out
      )
    }
    const tabs = (el as Record<string, unknown>).tabs
    if (Array.isArray(tabs)) {
      tabs.forEach((tab, j) => {
        if (
          tab &&
          typeof tab === "object" &&
          Array.isArray((tab as Record<string, unknown>).children)
        ) {
          collectElementPaths(
            (tab as Record<string, unknown>).children as Record<string, unknown>[],
            `${path}.tabs[${j}].children`,
            out
          )
        }
      })
    }
    const items = (el as Record<string, unknown>).items
    if (Array.isArray(items)) {
      items.forEach((it, j) => {
        if (
          it &&
          typeof it === "object" &&
          Array.isArray((it as Record<string, unknown>).children)
        ) {
          collectElementPaths(
            (it as Record<string, unknown>).children as Record<string, unknown>[],
            `${path}.items[${j}].children`,
            out
          )
        }
      })
    }
  }
  return out
}
